/**
 * App-wide UI click SFX — **one delegated listener**, not a touch to every call site. There is
 * no shared `<Button>` component in this codebase (every surface hand-rolls its own `<button>`),
 * so per-trigger feedback rides a single document-level `click` listener instead, firing
 * `sfxEngine.click()` gated to button-role elements.
 *
 * **What stays silent** (see {@link resolveSfxTarget}): a disabled button (a dead control
 * gives no feedback), and anything under a `data-no-sfx` opt-out — a general escape hatch
 * for marking a whole region silent.
 */
import { sfxEngine } from "./SfxEngine";

const INTERACTIVE_SELECTOR = 'button, [role="button"]';
const OPT_OUT_SELECTOR = "[data-no-sfx]";

/**
 * The minimal `Element` surface this module reads — just enough to decide, and just enough to
 * fake in a unit test (the repo ships no jsdom, so the predicates are kept DOM-free and tested
 * through structural fakes). A real DOM `Element` satisfies this.
 */
export type SfxCandidate = {
  closest(selectors: string): SfxCandidate | null;
  hasAttribute(name: string): boolean;
  getAttribute(name: string): string | null;
};

/**
 * Decide which element (if any) a pointer event should play UI SFX for — the pure, DOM-free
 * core of the delegated listener. Returns `null` (no sound, click or hover) when:
 *  - a `data-no-sfx` opt-out sits on the target or any ancestor (the general escape hatch) —
 *    this wins even over a button nested inside the opted-out region;
 *  - the target isn't inside a `button`/`[role="button"]`;
 *  - the resolved button is disabled — `disabled` attribute or `aria-disabled="true"`,
 *    checked on the resolved button element itself, not the raw event target.
 */
export function resolveSfxTarget(target: SfxCandidate): SfxCandidate | null {
  // Opt-out is checked first so it wins even over a button nested inside the opted-out region.
  if (target.closest(OPT_OUT_SELECTOR) !== null) return null;
  const btn = target.closest(INTERACTIVE_SELECTOR);
  if (btn === null) return null;
  if (btn.hasAttribute("disabled") || btn.getAttribute("aria-disabled") === "true") return null;
  return btn;
}

/** DOM entry point: guard the raw event target to an `Element`, then apply {@link resolveSfxTarget}. */
function sfxTargetFor(target: EventTarget | null): SfxCandidate | null {
  if (!(target instanceof Element)) return null;
  return resolveSfxTarget(target);
}

/**
 * Mount the delegated click SFX listener once, at app boot (`main.tsx`). Returns an unmount
 * function (unused in production — the listener lives for the app's lifetime — but kept for
 * test/HMR hygiene).
 */
export function installUiSfx(root: Document = document): () => void {
  const onClick = (e: MouseEvent) => {
    if (sfxTargetFor(e.target)) sfxEngine.click();
  };

  root.addEventListener("click", onClick);

  return () => {
    root.removeEventListener("click", onClick);
  };
}
